import React from 'react';
import { services } from '../mock';
import { ShieldCheck, Copyright, Lightbulb, Buildings, ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';

const iconMap = {
  Shield: ShieldCheck,
  FileText: Copyright,
  Lightbulb: Lightbulb,
  Building: Buildings
};

const serviceImages = {
  trademark: 'https://images.unsplash.com/photo-1750277120336-ca98ec2e2f90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHx0cmFkZW1hcmslMjByZWdpc3RyYXRpb24lMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85',
  copyright: 'https://images.unsplash.com/photo-1627518788331-b3b7fdaa382f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHx0cmFkZW1hcmslMjByZWdpc3RyYXRpb24lMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzUwNjY3Nzl8MA&ixlib=rb-4.1.0&q=85',
  patent: 'https://images.unsplash.com/photo-1758518730178-6e237bc8b87d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBsYXclMjBmaXJtJTIwb2ZmaWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NTA2Njc3OXww&ixlib=rb-4.1.0&q=85',
  company: 'https://images.unsplash.com/photo-1758518729922-3a9e7b62b4c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBsYXclMjBmaXJtJTIwb2ZmaWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NTA2Njc3OXww&ixlib=rb-4.1.0&q=85'
};

const ServicesSection = () => {
  const [emblaRef] = useEmblaCarousel({ loop: true, align: 'start' });

  return (
    <section data-testid="services-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">
            Our Practice Areas
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">
            Strategic Trademark & Brand Protection Solutions
          </h2>
          
           <p className="text-base text-slate-600 leading-relaxed max-w-5xl mx-auto">
Our team helps businesses secure trademarks for names, logos, slogans, and other valuable brand assets. We simplify the trademark registration process and provide ongoing monitoring, enforcement support, and strategic guidance to ensure your intellectual property remains protected as your business grows.          </p>
        </div>

        {/* Grid for desktop, slider for mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-xs:hidden">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            return (
              <Link
                key={service.id}
                to={service.link}
                data-testid={`service-card-${service.id}`}
                className="group relative overflow-hidden rounded-lg border border-slate-200 hover:shadow-2xl transition-all duration-500"
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img src={serviceImages[service.id]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/95 to-slate-900/70"></div>
                </div>
                
                <div className="relative z-10 p-8 min-h-[260px] flex flex-col justify-end">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#044B3B] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-white" weight="regular" />
                    </div>
                    <h3 className="text-2xl font-heading font-semibold text-white">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Slider for mobile (576px and below) */}
        <div className="overflow-hidden xs:hidden" ref={emblaRef}>
          <div className="flex gap-4">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to={service.link}
                  data-testid={`service-card-${service.id}`}
                  className="flex-[0_0_85%] group relative overflow-hidden rounded-lg border border-slate-200 hover:shadow-2xl transition-all duration-500"
                >
                  {/* Background Image */}
                  <div className="absolute inset-0">
                    <img src={serviceImages[service.id]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/95 to-slate-900/70"></div>
                  </div>
                  
                  <div className="relative z-10 p-8 min-h-[260px] flex flex-col justify-end">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#044B3B] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-white" weight="regular" />
                      </div>
                      <h3 className="text-2xl font-heading font-semibold text-white">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] group-hover:gap-3 transition-all">
                      Learn More <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
